import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Grid, makeStyles, useTheme, Popover } from '@material-ui/core';
import TableRow from '@mui/material/TableRow';
import { TableCell } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CloseIcon from '@mui/icons-material/Close';
import AntSwitch from '../../../../components/Common/Switch/AntSwitch';

const useStyles = makeStyles(theme => ({
  darkBorderLine: {
    borderRight: '1px solid #5C5C5C',
    height: '30px',
  },
  normalBorderLine: {
    borderRight: '1px solid #BEBEBE',
    height: '30px',
  },
  uniquePricingPopover: {
    minWidth: '280px',
  },
  uniquePricingHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: 0,
    backgroundColor: theme.palette.type === 'light' ? '#F5F7F7' : '#1A1A1A',
    borderBottom: theme.palette.type === 'light' ? '1px solid #9C27B02E' : '1px solid #BC75BC',
  },
  uniquePricingCloseBtn: {
    padding: '2px',
    color: '#BC75BC',
  },
  uniquePricingTable: {
    border: theme.palette.type === 'light' ? '1px solid #D9DEE2' : '1px solid #7D7D7D',
    padding: '5px',
    '& .MuiTableCell-root': {
      padding: '8px 12px',
      fontSize: '14px',
      borderBottom: theme.palette.type === 'light' ? '1px solid #D9DEE2' : '1px solid #7D7D7D',
    },
  },
  uniquePricingTableHead: {
    '& .MuiTableCell-root': {
      fontWeight: 400,
      color: theme.palette.type === 'light' ? '#3C3C3C' : '#B0B0B0',
      backgroundColor: theme.palette.type === 'light' ? '#D9DEE2' : '#1A1A1A',
    },
  },
  uniquePricingTableBody: {
    '& .MuiTableCell-root': {
      color: theme.palette.type === 'light' ? '#262626' : '#B0B0B0',
    },
  },
  uniquePricingTableCell: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      width: '1px',
      backgroundColor: '#BEBEBE',
    },
  },
  localOfferIconClickable: {
    cursor: 'pointer',
    '&:hover': {
      color: '#9C27B0',
    },
  },
}));

const UniquePricingModal = props => {
  const { rowInfo, handleGroupAttrEdit, disabled } = props;
  const [isTMRUnique, setIsTMRUnique] = useState(rowInfo?.tmr_unique_pricing?.status === 'on');
  const [isSHUnique, setIsSHUnique] = useState(rowInfo?.sh_unique_pricing?.status === 'on');
  const [isVSUnique, setIsVSUnique] = useState(rowInfo?.vs_unique_pricing?.status === 'on');
  const [uniquePricingAnchorEl, setUniquePricingAnchorEl] = useState(null);

  // Check if unique pricing data exists for any marketplace
  const hasTMRUniquePricing = rowInfo?.tmr_unique_pricing?.markupdown_value > 0;
  const hasSHUniquePricing = rowInfo?.sh_unique_pricing?.markupdown_value > 0;
  const hasVSUniquePricing = rowInfo?.vs_unique_pricing?.markupdown_value > 0;
  const hasAnyUniquePricing = hasTMRUniquePricing || hasSHUniquePricing || hasVSUniquePricing;

  // Master toggle state - all on if all available marketplaces are on
  const [isAllUnique, setIsAllUnique] = useState(() => {
    const tmrOn = !hasTMRUniquePricing || isTMRUnique;
    const shOn = !hasSHUniquePricing || isSHUnique;
    const vsOn = !hasVSUniquePricing || isVSUnique;
    return tmrOn && shOn && vsOn && hasAnyUniquePricing;
  });

  // Sync master toggle when individual toggles change
  useEffect(() => {
    const tmrOn = !hasTMRUniquePricing || isTMRUnique;
    const shOn = !hasSHUniquePricing || isSHUnique;
    const vsOn = !hasVSUniquePricing || isVSUnique;
    setIsAllUnique(tmrOn && shOn && vsOn && hasAnyUniquePricing);
  }, [
    isTMRUnique,
    isSHUnique,
    isVSUnique,
    hasTMRUniquePricing,
    hasSHUniquePricing,
    hasVSUniquePricing,
    hasAnyUniquePricing,
  ]);

  const classes = useStyles();
  const theme = useTheme();

  const canOpenUniquePricing = hasAnyUniquePricing && !disabled;

  const handleUniquePricingClick = event => {
    event.stopPropagation();
    setUniquePricingAnchorEl(event.currentTarget);
  };

  const handleUniquePricingClose = () => {
    setUniquePricingAnchorEl(null);
  };

  const isUniquePricingPopoverOpen = Boolean(uniquePricingAnchorEl);

  const buildUniquePricingValue = (overrides = {}) => {
    const tmrUniqueValue = rowInfo?.tmr_unique_pricing;
    const shUniqueValue = rowInfo?.sh_unique_pricing;
    const vsUniqueValue = rowInfo?.vs_unique_pricing;
    const uniquePricingValue = {};
    if (tmrUniqueValue) uniquePricingValue.TicketsNow = { ...tmrUniqueValue, ...overrides.TicketsNow };
    if (shUniqueValue) uniquePricingValue.StubHub = { ...shUniqueValue, ...overrides.StubHub };
    if (vsUniqueValue) uniquePricingValue.Vivid = { ...vsUniqueValue, ...overrides.Vivid };
    return uniquePricingValue;
  };

  const handleTMRUniqueToggle = event => {
    event.stopPropagation();
    const newValue = event.target.checked ? 'on' : 'off';
    setIsTMRUnique(event.target.checked);
    handleGroupAttrEdit('unique_pricing')(null, buildUniquePricingValue({ TicketsNow: { status: newValue } }));
  };

  const handleSHUniqueToggle = event => {
    event.stopPropagation();
    const newValue = event.target.checked ? 'on' : 'off';
    setIsSHUnique(event.target.checked);
    handleGroupAttrEdit('unique_pricing')(null, buildUniquePricingValue({ StubHub: { status: newValue } }));
  };

  const handleVSUniqueToggle = event => {
    event.stopPropagation();
    const newValue = event.target.checked ? 'on' : 'off';
    setIsVSUnique(event.target.checked);
    handleGroupAttrEdit('unique_pricing')(null, buildUniquePricingValue({ Vivid: { status: newValue } }));
  };

  const handleAllUniqueToggle = event => {
    event.stopPropagation();
    const { checked } = event.target;
    const status = checked ? 'on' : 'off';
    setIsAllUnique(checked);
    const overrides = {};
    if (hasTMRUniquePricing) {
      setIsTMRUnique(checked);
      overrides.TicketsNow = { status };
    }
    if (hasSHUniquePricing) {
      setIsSHUnique(checked);
      overrides.StubHub = { status };
    }
    if (hasVSUniquePricing) {
      setIsVSUnique(checked);
      overrides.Vivid = { status };
    }
    handleGroupAttrEdit('unique_pricing')(null, buildUniquePricingValue(overrides));
  };

  return (
    <Grid
      item
      style={{ paddingTop: '6px', marginTop: '1px' }}
      className={theme.palette.type === 'dark' ? classes.darkBorderLine : classes.normalBorderLine}
    >
      <Grid container justifyContent="center" alignContent="center" style={{ paddingRight: '10px' }}>
        <IconButton
          size="small"
          style={{ padding: 0 }}
          disabled={!canOpenUniquePricing}
          className={canOpenUniquePricing ? classes.localOfferIconClickable : undefined}
          onClick={canOpenUniquePricing ? handleUniquePricingClick : undefined}
        >
          <LocalOfferIcon
            style={{
              fontSize: '20px',
              color: canOpenUniquePricing ? '#9C27B0' : theme.palette.action.disabled,
            }}
          />
        </IconButton>

        <Popover
          open={isUniquePricingPopoverOpen}
          anchorEl={uniquePricingAnchorEl}
          onClose={handleUniquePricingClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          onClick={e => e.stopPropagation()}
        >
          <Box className={classes.uniquePricingPopover}>
            <Box className={classes.uniquePricingHeader}>
              <IconButton size="small" onClick={handleUniquePricingClose} className={classes.uniquePricingCloseBtn}>
                <CloseIcon fontSize="small" style={{ color: theme.palette.type === 'light' ? '#9C27B0' : '#BC75BC' }} />
              </IconButton>
            </Box>
            <Box style={{ padding: '10px' }}>
              <Table size="small" className={classes.uniquePricingTable}>
                <TableHead className={classes.uniquePricingTableHead}>
                  <TableRow>
                    <TableCell>
                      <AntSwitch checked={isAllUnique} onChange={handleAllUniqueToggle} />
                    </TableCell>
                    <TableCell>Marketplace</TableCell>
                    <TableCell>Comp Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.uniquePricingTableBody}>
                  {hasTMRUniquePricing && (
                    <TableRow>
                      <TableCell>
                        <AntSwitch checked={isTMRUnique} onChange={handleTMRUniqueToggle} />
                      </TableCell>
                      <TableCell className={classes.uniquePricingTableCell}>TMR</TableCell>
                      <TableCell>{`$${rowInfo?.tmr_unique_pricing?.comp_price?.toFixed(2)}`}</TableCell>
                    </TableRow>
                  )}
                  {hasSHUniquePricing && (
                    <TableRow>
                      <TableCell>
                        <AntSwitch checked={isSHUnique} onChange={handleSHUniqueToggle} />
                      </TableCell>
                      <TableCell className={classes.uniquePricingTableCell}>SH</TableCell>
                      <TableCell>{`$${rowInfo?.sh_unique_pricing?.comp_price?.toFixed(2)}`}</TableCell>
                    </TableRow>
                  )}
                  {hasVSUniquePricing && (
                    <TableRow>
                      <TableCell>
                        <AntSwitch checked={isVSUnique} onChange={handleVSUniqueToggle} />
                      </TableCell>
                      <TableCell className={classes.uniquePricingTableCell}>VS</TableCell>
                      <TableCell>{`$${rowInfo?.vs_unique_pricing?.comp_price?.toFixed(2)}`}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Popover>
      </Grid>
    </Grid>
  );
};

UniquePricingModal.propTypes = {
  rowInfo: PropTypes.object.isRequired,
  handleGroupAttrEdit: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default UniquePricingModal;
